import type {FeatureAppDefinition} from '@feature-hub/core';
import type {ReactFeatureApp} from '@feature-hub/react';
import * as React from 'react';
import {ErrorBoundary} from 'react-error-boundary';
import {FeatureAppEnvProvider} from '../hooks/use-feature-app-env';
import type {FeatureAppDependencies} from '../hooks/use-feature-app-env';
import {createPromiseWrapper} from './create-loading-promise-wrapper';

type AppProps = {
  /**
   * Callback when the feature app finished loading
   */
  done: () => void;
};

interface CreateFeatureAppConfig {
  App: (props: AppProps) => JSX.Element;
  fallback?: JSX.Element;
  loading?: JSX.Element;
}

/**
 * Feature Hub Feature App Definition for webpack index.tsx entry in feature apps
 * see `featureAppConfig` in `webpack.config.js` in feature apps
 */
export function createFeatureAppDefinition({
  App,
  fallback,
  loading,
}: CreateFeatureAppConfig): FeatureAppDefinition<
  ReactFeatureApp,
  FeatureAppDependencies,
  any
> {
  // List of the feature services that currently are used in https://bitbucket.org/smart_eco-platform/smart-website/src/main/
  return {
    dependencies: {
      featureServices: {
        'adb-context-service': `^2.0.0`,
        'endpoint-directory': `^3.0.0`,
        'locale-service': `^3.0.0`,
        'login-service': `^3.0.0`,
        'page-context-service': `^1.0.0`,
        'payment-config-service': `^1.0.0`,
        'price-preferences-provider': `^1.0.0`,
        'pub-sub': `^1.0.0`,
        's2:history': `^3.0.0`,
        's2:logger': `^1.0.0`,
        'selected-vehicle-service': `^1.0.0`,
      },
    },

    create: (env) => {
      const loadingPromiseWrapper = createPromiseWrapper<void>();

      const done = (error?: unknown) => {
        if (env?.done) {
          env.done();
        }
        if (error) {
          loadingPromiseWrapper.reject(error);
          return;
        }
        loadingPromiseWrapper.resolve();
      };
      return {
        loadingPromise: loadingPromiseWrapper.promise,
        render: () => {
          return (
            <React.Suspense fallback={loading}>
              <FeatureAppEnvProvider env={env}>
                <ErrorBoundary fallback={fallback ?? <div>Error</div>}>
                  <App done={done} />
                </ErrorBoundary>
              </FeatureAppEnvProvider>
            </React.Suspense>
          );
        },
      };
    },
  };
}
